import { debounce } from '../../core/utils/debounce.js';
import { createServiceJourneyApi } from '../../core/api/serviceJourneyApi.js';
import { applyPartnerBranding, hydratePartnerBranding, resolvePartnerContext } from '../../core/utils/partnerBranding.js';
import { getConsentedAcquisitionSignals } from '../../core/growth/acquisition.js';
import { OFFICE_PHONE, buildQuestions, formatPounds, postcodeFrom, splitName, stopCopy, stopFor } from './flow.js';

// The public heat pump servicing journey. Webflow owns the markup and styling
// (docs/webflow-service-journey.md); this store owns the steps. Two doors:
// ?type=one-off (book a service) and ?type=plan (monthly plan); no type shows
// the choice first. Nothing is paid here: the office confirms, then the
// customer pays or sets up the Direct Debit from their Dwellow account.

function resolveConfig(root) {
    const baseUrl = root?.dataset?.apiBase || root?.dataset?.baseUrl || 'https://api.dwellow.co.uk';
    const { partnerId, source: partnerSource } = resolvePartnerContext(root);
    const siteKey = root?.dataset?.siteKey || '';
    const termsUrl = root?.dataset?.termsUrl || '/policies/air-source-heat-pump-servicing-terms';
    // Customers we already look after book from their account, wherever they live.
    const hubLoginUrl = root?.dataset?.hubLoginUrl || 'https://home.dwellow.co.uk/login';
    return { baseUrl, partnerId, partnerSource, siteKey, termsUrl, hubLoginUrl };
}

function readType() {
    if (typeof window === 'undefined') return null;
    const value = new URLSearchParams(window.location.search).get('type');
    if (value === 'plan') return 'plan';
    if (value === 'one-off' || value === 'service') return 'one-off';
    return null;
}

function track(name, props) {
    try {
        if (typeof window !== 'undefined' && typeof window.plausible === 'function') window.plausible(name, { props });
    } catch {
        // Analytics never blocks the journey.
    }
}

export function registerServiceJourney() {
    if (typeof document === 'undefined') return;

    // Alpine can initialise the root more than once (Webflow interactions,
    // the late-load fallback); every pass gets the same store.
    const createStore = () => {
        if (window.__serviceJourneyState) return window.__serviceJourneyState;
        window.__serviceJourneyState = buildStore();
        return window.__serviceJourneyState;
    };

    const buildStore = () => ({
        step: 'choose',
        type: null,
        loading: false,
        error: null,
        initialized: false,
        api: null,
        partnerId: null,
        siteKey: '',
        termsUrl: '',
        hubLoginUrl: '',
        branding: null,
        officePhone: OFFICE_PHONE,
        config: null,
        postcode: '',
        questionIndex: 0,
        answers: {},
        history: [],
        stopReason: null,
        addressQuery: '',
        addressResults: [],
        addressSearching: false,
        address: null,
        fullName: '',
        email: '',
        phone: '',
        conditionNote: '',
        termsAgreed: false,
        reference: '',
        debouncedAddressSearch: null,
        // Bound by the cloned estimate markup; this journey has no helper boxes or transitions.
        isTransitioning: false,
        showHelperSection: false,
        activeHelperContent: null,

        init() {
            if (this.initialized) return;
            this.initialized = true;
            const root = this.$root;
            const { baseUrl, partnerId, siteKey, termsUrl, hubLoginUrl } = resolveConfig(root);
            this.hubLoginUrl = hubLoginUrl;
            this.partnerId = partnerId;
            this.siteKey = siteKey;
            this.termsUrl = termsUrl;
            this.api = createServiceJourneyApi({ baseUrl, siteKey });
            this.branding = applyPartnerBranding({}, root);
            hydratePartnerBranding({ api: this.api, partnerId, root })
                .then(result => { this.branding = result?.branding ?? this.branding; })
                .catch(() => {});
            this.debouncedAddressSearch = debounce(() => this.searchAddress(), 200);
            const type = readType();
            if (type) {
                this.type = type;
                this.step = 'postcode';
            }
            this.api.check({}).then(result => { this.config = result?.config ?? null; }).catch(() => {});
            track('Service journey started', { type: this.type || 'choose' });
        },

        // ---- labels for the page -------------------------------------------------
        get makes() { return this.config?.supportedManufacturers?.length ? this.config.supportedManufacturers : ['Vaillant', 'Ideal']; },
        get oneOffPriceLabel() { return this.config ? formatPounds(this.config.oneOffPriceIncVat) : ''; },
        get monthlyPriceLabel() { return this.config ? formatPounds(this.config.monthlyInstalmentIncVat) : ''; },
        get instalments() { return this.config?.monthlyInstalments ?? 12; },
        get priceLabel() { return this.type === 'plan' ? `${this.monthlyPriceLabel} a month` : this.oneOffPriceLabel; },
        get priceNote() {
            return this.type === 'plan'
                ? `${this.instalments} payments by Direct Debit. We book your service once the first payment is in.`
                : 'Including VAT. You pay when you confirm the date we offer.';
        },
        get stop() { return stopCopy(this.stopReason, { makes: this.makes }); },

        // ---- branding for the navbar and footer copied from the estimate page --
        get brandName() { return this.branding?.name || 'dwellow'; },
        get brandLogoUrl() { return this.branding?.logoUrl || ''; },
        get brandLogoLinkUrl() { return this.branding?.logoLinkUrl || ''; },
        get showWhichLogo() { return this.branding?.isWhich === true; },
        get showMadeInBritain() { return this.branding?.isMadeInBritain === true; },
        get showContactExplain() { return this.branding?.isContactExplain === true; },
        get showGenericNav() { return this.branding?.isGenericNav === true; },
        get showDwellowTrustpilot() { return this.branding?.isDwellowTrustpilot === true; },
        // The estimate page never shows the partner footer line; nor does this one.
        get showPoweredByDwellow() { return false; },

        // ---- progress ------------------------------------------------------------
        get questions() { return buildQuestions({ type: this.type, makes: this.makes }); },
        get currentQuestion() { return this.step === 'question' ? this.questions[this.questionIndex] ?? null : null; },
        get stepOrder() { return ['postcode', ...this.questions.map(q => `question:${q.id}`), 'address', 'contact', 'review']; },
        get stepKey() { return this.step === 'question' ? `question:${this.currentQuestion?.id}` : this.step; },
        get currentStepIndex() { return Math.max(1, this.stepOrder.indexOf(this.stepKey) + 1); },
        get totalSteps() { return this.stepOrder.length; },
        get progressPct() { return Math.round((this.currentStepIndex / this.totalSteps) * 100); },
        get showProgress() { return this.stepOrder.includes(this.stepKey); },
        get canGoBack() { return this.history.length > 0 && !this.loading && this.step !== 'done'; },

        go(step, questionIndex = this.questionIndex) {
            this.history.push({ step: this.step, questionIndex: this.questionIndex });
            this.step = step;
            this.questionIndex = questionIndex;
            this.error = null;
            track('Service journey step', { step: this.stepKey, type: this.type || '' });
        },

        back() {
            const previous = this.history.pop();
            if (!previous) return;
            this.step = previous.step;
            this.questionIndex = previous.questionIndex;
            this.stopReason = null;
            this.error = null;
        },

        choose(type) {
            this.type = type === 'plan' ? 'plan' : 'one-off';
            this.go('postcode');
        },

        async submitPostcode() {
            const postcode = this.postcode.trim();
            if (!postcode) {
                this.error = 'Enter your postcode.';
                return;
            }
            this.loading = true;
            this.error = null;
            try {
                const result = await this.api.check({ postcode });
                if (result?.config) this.config = result.config;
                if (result?.coverage?.covered) {
                    this.addressQuery = postcode;
                    this.go('question', 0);
                } else {
                    this.stopReason = result?.coverage?.reason || 'area';
                    this.go('stop');
                }
            } catch {
                this.error = `We could not check that just now. Try again, or call us on ${OFFICE_PHONE}.`;
            } finally {
                this.loading = false;
            }
        },

        optionIsSelected(value) {
            return this.currentQuestion ? this.answers[this.currentQuestion.id] === value : false;
        },

        handleAnswer(value) {
            const question = this.currentQuestion;
            if (!question) return;
            this.answers = { ...this.answers, [question.id]: value };
            const stop = stopFor(question.id, value);
            if (stop) {
                this.stopReason = stop;
                track('Service journey stopped', { reason: stop });
                this.go('stop');
                return;
            }
            this.nextQuestion();
        },

        nextQuestion() {
            if (this.questionIndex + 1 < this.questions.length) {
                this.go('question', this.questionIndex + 1);
                return;
            }
            this.go('address');
            this.searchAddress();
        },

        onAddressInput() {
            this.address = null;
            this.debouncedAddressSearch?.();
        },

        async searchAddress() {
            const query = this.addressQuery.trim();
            if (query.length < 3) {
                this.addressResults = [];
                return;
            }
            this.addressSearching = true;
            try {
                const result = await this.api.searchAddress({ query });
                this.addressResults = Array.isArray(result?.results) ? result.results.filter(item => item.udprn) : [];
            } catch {
                this.addressResults = [];
            } finally {
                this.addressSearching = false;
            }
        },

        // Takes the result or its udprn, so markup cloned from the estimate page binds as is.
        async selectAddress(itemOrUdprn) {
            const item = typeof itemOrUdprn === 'object' ? itemOrUdprn : this.addressResults.find(result => String(result.udprn) === String(itemOrUdprn));
            if (!item?.udprn) return;
            this.address = { summaryLine: item.summaryline || '', postcode: postcodeFrom(item) || this.postcode.trim().toUpperCase(), udprn: String(item.udprn) };
            this.go('contact');
        },

        submitContact() {
            if (!splitName(this.fullName)) {
                this.error = 'Enter your first and last name.';
                return;
            }
            if (!/^\S+@\S+\.\S+$/.test(this.email.trim())) {
                this.error = 'Enter a valid email address.';
                return;
            }
            if (this.phone.replace(/\D/g, '').length < 10) {
                this.error = 'Enter a phone number we can call.';
                return;
            }
            this.go('review');
        },

        get reviewRows() {
            const question = id => this.questions.find(q => q.id === id);
            const label = (id, value) => question(id)?.options?.find(option => option.value === value)?.label ?? value;
            return [
                { label: 'Heat pump', value: [this.answers.make, this.answers.model].filter(Boolean).join(' ') },
                { label: 'Fitted', value: label('fitted', this.answers.fitted) },
                { label: 'Hot water cylinder', value: label('cylinder', this.answers.cylinder) },
                { label: 'Address', value: this.address?.summaryLine ?? '' },
                ...(this.type === 'one-off' ? [{ label: 'When suits you', value: this.answers.timePreference ?? '' }] : [])
            ];
        },

        get selectedAddressUdprn() { return this.address?.udprn ?? null; },

        get somethingNotRight() { return this.answers.condition === 'not-right'; },

        async send() {
            if (!this.termsAgreed) {
                this.error = 'Tick to agree to the servicing terms.';
                return;
            }
            const name = splitName(this.fullName);
            this.loading = true;
            this.error = null;
            try {
                const result = await this.api.submit({
                    type: this.type,
                    kind: this.answers.kind,
                    make: this.answers.make,
                    model: this.answers.model || '',
                    fitted: this.answers.fitted,
                    lastServiced: this.answers.lastServiced,
                    cylinder: this.answers.cylinder,
                    condition: this.answers.condition,
                    conditionNote: this.conditionNote.trim(),
                    timePreference: this.answers.timePreference || 'As soon as possible',
                    address: this.address,
                    customer: { ...name, email: this.email.trim(), phone: this.phone.trim() },
                    termsVersion: this.config?.terms?.version || '',
                    partnerId: this.partnerId || '',
                    acquisitionSignals: getConsentedAcquisitionSignals() || null
                });
                this.reference = result?.reference || '';
                track('Service journey sent', { type: this.type });
                this.history = [];
                this.step = 'done';
            } catch (error) {
                const reason = String(error?.message || '').match(/"reason":"([a-z-]+)"/)?.[1];
                if (reason) {
                    this.stopReason = reason;
                    this.go('stop');
                } else {
                    this.error = `We could not send your request. Try again, or call us on ${OFFICE_PHONE}.`;
                }
            } finally {
                this.loading = false;
            }
        },

        get nextSteps() {
            const steps = [];
            if (this.answers.kind === 'not-sure') steps.push('We check we can service your heat pump.');
            if (this.type === 'plan') {
                steps.push('We confirm your request and email you a link to set up your Direct Debit.');
                steps.push('Once your first payment is in, we offer you a date.');
            } else {
                steps.push('We offer you a date, usually within 2 working days.');
                steps.push(`You pay ${this.oneOffPriceLabel || 'for the service'} to confirm it. We hold the date for 3 days.`);
            }
            steps.push(`An engineer services your heat pump${this.answers.cylinder === 'yes' ? ' and cylinder' : ''}.`);
            steps.push('Your service record is in your Dwellow account.');
            return steps;
        },

        restart() {
            Object.assign(this, { step: readType() ? 'postcode' : 'choose', type: readType(), questionIndex: 0, answers: {}, history: [], stopReason: null, address: null, addressResults: [], termsAgreed: false, error: null });
        }
    });

    if (typeof window !== 'undefined') {
        window.serviceJourney = createStore;
    }

    const refreshAlpine = () => {
        if (!window.Alpine || typeof window.Alpine.initTree !== 'function') return;
        const root = document.querySelector('[data-journey="service-journey"]');
        if (root) window.Alpine.initTree(root);
    };

    const register = () => {
        if (!window.Alpine || typeof window.Alpine.data !== 'function') return;
        window.Alpine.data('serviceJourney', createStore);
        refreshAlpine();
    };

    if (window.Alpine) register();
    else document.addEventListener('alpine:init', register);
    document.addEventListener('DOMContentLoaded', refreshAlpine);
}

registerServiceJourney();
